import React from 'react';
import { connect } from 'react-redux';
import { useFeatureValue } from '@growthbook/growthbook-react';

import { deepClone } from '@gumtree/shared/src/model/generic.model';
import { ExperimentVariant, GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { Container, Grid } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';

import { copyright, globalSites, sections as footerSections, apps } from './footer-data.json';
import FooterCopyright from './footer-copyright';
import FooterLinks from './footer-links';
import FooterAppLinks from './footer-app-links';
import FooterSocial from './footer-social';
import ModernFooter from './modern-footer';
import { trackAndNavigate } from '../common/common-actions';
import ErrorBoundary from '../error-boundary';
import type { ShellState } from '../reducers/common';

import { FooterBlock, FooterDividerStyle } from './style';

const Divider = () => (
    <Grid container>
        <Grid item col={12} colXl={10} offsetXl={1}>
            <div css={FooterDividerStyle} />
        </Grid>
    </Grid>
);

type FooterContentProps = Props & {
    isVotRedesign: boolean;
    mhcVariant?: ExperimentVariant;
};

const FooterContent: React.FC<FooterContentProps> = ({
    category,
    device,
    isVotRedesign,
    mhcVariant,
    trackAndNavigate,
}) => {
    const sections = deepClone(footerSections);
    const showModernFooter =
        isVotRedesign || (device.type === 'mobile' && mhcVariant !== ExperimentVariant.VARIANT_A);

    if (showModernFooter) {
        return (
            <ErrorBoundary>
                <ModernFooter
                    apps={apps}
                    copyrightLinks={copyright.links}
                    device={device}
                    isMotors={category?.l1Category === 'cars-vans-motorbikes'}
                    isVotRedesign={isVotRedesign}
                    partners={globalSites.items}
                    sections={sections}
                />
            </ErrorBoundary>
        );
    }

    if (device.type === 'mobile') {
        sections.pop();
    }

    return (
        <ErrorBoundary>
            <FooterBlock {...qaAttribute('footer-container')}>
                <Container isSection>
                    <nav>
                        {device.type === 'mobile' && (
                            <>
                                <FooterAppLinks
                                    sections={apps}
                                    device={device}
                                    trackAndNavigate={trackAndNavigate}
                                />
                            </>
                        )}
                        <FooterLinks
                            sections={sections}
                            device={device}
                            trackAndNavigate={trackAndNavigate}
                        />
                        <Divider />
                        <Grid container>
                            <Grid item col={12} colL={3} colXl={3} offsetL={5} offsetXl={5}>
                                <FooterSocial />
                            </Grid>
                        </Grid>
                        <Divider />
                        <FooterCopyright links={copyright.links} />
                    </nav>
                </Container>
            </FooterBlock>
        </ErrorBoundary>
    );
};

const MobileFooter: React.FC<Props> = (props) => {
    const mhcVariant = useFeatureValue(
        GrowthBookFeature.MHC,
        ExperimentVariant.VARIANT_A
    ) as ExperimentVariant;

    return <FooterContent {...props} isVotRedesign mhcVariant={mhcVariant} />;
};

export const Footer: React.FC<Props> = (props) => {
    if (props.device.type === 'mobile') {
        return <MobileFooter {...props} />;
    }

    return <FooterContent {...props} isVotRedesign />;
};

const mapState = ({ baseConfig: { category, device, pageType } }: ShellState) => ({
    category,
    device,
    pageType,
});

const mapDispatch = (dispatch) => ({
    trackAndNavigate: (event: string, url: string, eventLabel?: string) => {
        dispatch(trackAndNavigate(event, url, eventLabel));
    },
});

export type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect(mapState, mapDispatch)(Footer);
